import React, { useState, useRef, useEffect } from 'react';
import DehazeIcon from "@mui/icons-material/Dehaze";
import ChatIcon from "@mui/icons-material/Chat";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import Plot from 'react-plotly.js';

const ChatWindow = () => {  
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [conversationId, setConversationId] = useState(null);  
  const [currentThreadId, setCurrentThreadId] = useState(null);  
  const [chatThreads, setChatThreads] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [threadToDelete, setThreadToDelete] = useState(null);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  };

  const toggleMobileSidebar = () => {
    setIsMobileSidebarOpen(!isMobileSidebarOpen);
  };

  const closeMobileSidebar = () => {
    setIsMobileSidebarOpen(false);
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  
  useEffect(() => {
    initializeSession();
    loadChatThreads();
  }, []);

  const initializeSession = async () => {
    try {
      const response = await fetch('/api/chat/session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userEmail: 'bhanuka.seneviratne@sherbetrewards.com' }),
      });
      const session = await response.json();
      setSessionId(session.id);
    } catch (error) {
      console.error('Error initializing session:', error);    
    }
  };

  const renderElement = (element) => {
    switch (element.type) {
      case 'table':
        return (
          <div key={element.id} className="chat-table-container">
            {element.metadata?.title && (
              <h6 className="table-title">{element.metadata.title}</h6>
            )}
            <div className="table-responsive">
              <table className="table table-striped table-hover table-sm">
                <thead className="table-dark">
                  <tr>
                    {element.content.headers?.map((header, index) => (
                      <th key={index}>{header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {element.content.rows?.map((row, index) => (
                    <tr key={index}>
                      {element.content.headers?.map((header, cellIndex) => (
                        <td key={cellIndex}>{row[header]}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="table-footer">
              <small className="text-muted">
                <i className="fas fa-info-circle me-1"></i>
                Showing {element.content.totalRows} {element.content.totalRows === 1 ? 'result' : 'results'}
              </small>            
            </div>
          </div>
        );
      
      case 'dataframe':
        return (
          <div key={element.id} className="chat-table-container">
            {element.metadata?.title && (
              <h6 className="table-title">{element.metadata.title}</h6>
            )}
            <div className="table-responsive">
              <table className="table table-striped table-hover table-sm">
                <thead className="table-dark">
                  <tr>
                    {element.content.headers?.map((header, index) => (
                      <th key={index}>{header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {element.content.rows?.map((row, index) => (
                    <tr key={index}>
                      {element.content.headers?.map((header, cellIndex) => (
                        <td key={cellIndex}>{row[header]}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="table-footer">
              <small className="text-muted">
                <i className="fas fa-database me-1"></i>
                DataFrame with {element.content.totalRows} {element.content.totalRows === 1 ? 'record' : 'records'}
              </small>
            </div>
          </div>
        );
      
      case 'chart':
        return (
          <div key={element.id} className="chat-chart-container">
            {element.metadata?.title && (
              <h6 className="chart-title">{element.metadata.title}</h6>
            )}
            <div className="chart-wrapper">
              <img 
                src={element.content} 
                alt="Data Visualization" 
                className="chart-image"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <div className="chart-error" style={{display: 'none'}}>
                <i className="fas fa-chart-bar chart-icon"></i>
                <p>Chart visualization is available at: <a href={element.content} target="_blank" rel="noopener noreferrer">View Chart</a></p>
              </div>
            </div>
          </div>
        );
        
      case 'plotly':
        return (
          <div key={element.id} className="chat-plotly-container">
            {element.metadata?.title && (
              <h6 className="plotly-title">{element.metadata.title}</h6>
            )}
            <div className="plotly-wrapper">
              <Plot
                data={element.content.data || []}
                layout={{
                  ...element.content.layout,
                  autosize: true,
                  responsive: true
                }}
                config={{
                  responsive: true,
                  displayModeBar: true,
                  displaylogo: false,
                  modeBarButtonsToRemove: ['pan2d', 'lasso2d', 'select2d']
                }}
                style={{ width: '100%', height: '400px' }}
                useResizeHandler={true}
              />
            </div>
          </div>
        );
      
      case 'link':
        return (
          <div key={element.id} className="chat-link-container">
            {element.metadata?.title && (
              <h6 className="link-title">{element.metadata.title}</h6>
            )}
            <div className="link-wrapper">
              <a 
                href={element.content.proxyUrl || element.content.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="download-link"
              >
                <i className="fas fa-download me-2"></i>
                {element.content.title}
              </a>
              {element.content.description && (
                <p className="link-description">{element.content.description}</p>
              )}
            </div>
          </div>
        );
      
      case 'code':
        return (
          <div key={element.id} className="chat-code-container">
            <pre><code>{element.content}</code></pre>
          </div>
        );
      
      default:
        return (
          <div key={element.id} className="chat-text">
            {element.metadata?.title && (
              <div className="element-title">{element.metadata.title}</div>
            )}
            <div dangerouslySetInnerHTML={{ 
              __html: element.content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') 
            }} />
          </div>
        );
    }
  };

  // Thread management functions
  const loadChatThreads = async () => {
    try {
      const response = await fetch('/api/chat/threads');
      if (response.ok) {
        const threads = await response.json();
        setChatThreads(threads);
      }
    } catch (error) {
      console.error('Error loading chat threads:', error);
    }
  };
  
  const createNewThread = async (title = 'New Chat') => {
    try {
      const response = await fetch('/api/chat/threads', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title,
          userId: 'current-user', // Replace with actual user ID
        }),
      });

      if (response.ok) {
        const newThread = await response.json();
        console.log('New thread created:', newThread);
        
        // Automatically select the new thread
        setCurrentThreadId(newThread.id);
        setMessages([]);
        setConversationId(null);
        
        // Refresh the threads list to show the new thread
        await loadChatThreads();
        
        // Close mobile sidebar after creating thread
        closeMobileSidebar();
        
        return newThread;
      } else {
        console.error('Failed to create thread:', response.status);
      }
    } catch (error) {
      console.error('Error creating new thread:', error);    
    }
    return null;
  };
  
  const deleteThread = async (threadId, event) => {
    // Prevent the thread from being selected when delete button is clicked
    event.stopPropagation();
    
    // Show modal instead of browser confirm
    setThreadToDelete(threadId);
    setShowDeleteModal(true);
  };

  const confirmDeleteThread = async () => {
    if (!threadToDelete) return;

    try {
      const response = await fetch(`/api/chat/threads/${threadToDelete}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        console.log('Thread deleted:', threadToDelete);
        
        // If the deleted thread was currently selected, clear the selection
        if (currentThreadId === threadToDelete) {
          setCurrentThreadId(null);
          setMessages([]);
          setConversationId(null);
        }
        
        // Refresh the threads list
        await loadChatThreads();
      } else {
        console.error('Failed to delete thread:', response.status);
        alert('Failed to delete thread. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting thread:', error);
      alert('Error deleting thread. Please try again.');
    } finally {
      // Close modal and reset state
      setShowDeleteModal(false);
      setThreadToDelete(null);
    }
  };

  const cancelDeleteThread = () => {
    setShowDeleteModal(false);
    setThreadToDelete(null);
  };

  const loadThread = async (threadId) => {
    try {
      const response = await fetch(`/api/chat/threads/${threadId}/formatted`);
      if (response.ok) {
        const thread = await response.json();
        setCurrentThreadId(threadId);
        
        // Use the pre-formatted messages from the backend
        const uiMessages = thread.formattedMessages.map(msg => ({
          id: msg.id,
          type: msg.type,
          content: msg.type === 'user' ? msg.content : undefined,
          elements: msg.type === 'assistant' ? msg.elements : undefined,
          timestamp: new Date(msg.timestamp),
        }));
        
        setMessages(uiMessages);
        // Close mobile sidebar after selecting thread
        closeMobileSidebar();
      }
    } catch (error) {
      console.error('Error loading thread:', error);
      // Fallback to original endpoint if formatted fails
      try {
        const response = await fetch(`/api/chat/threads/${threadId}`);
        if (response.ok) {
          const thread = await response.json();
          setCurrentThreadId(threadId);
          
          // Fallback parsing logic
          const uiMessages = thread.messages
            .filter(msg => msg.role === 'user') // Only show user messages as fallback
            .map(msg => ({
              id: msg.id,
              type: 'user',
              content: msg.content,
              timestamp: new Date(msg.createdAt),
            }));
          
          setMessages(uiMessages);
          // Close mobile sidebar after selecting thread
          closeMobileSidebar();
        }
      } catch (fallbackError) {
        console.error('Error loading thread (fallback):', fallbackError);
      }
    }
  };  

  const handleSendMessageToThread = async () => {
    if (!inputMessage.trim()) return;

    // Create new thread if none exists
    let threadId = currentThreadId;
    if (!threadId) {
      console.log('No thread selected, creating new thread...');
      const newThread = await createNewThread(`Chat: ${inputMessage.substring(0, 30)}...`);
      threadId = newThread?.id;
      if (!threadId) {
        console.error('Failed to create new thread');
        return;
      }
      console.log('Successfully created and selected new thread:', threadId);
    }

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: inputMessage,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    const currentMessage = inputMessage;
    setInputMessage('');
    setIsLoading(true);

    // Add immediate typing indicator
    const typingMessage = {
      id: `typing-${Date.now()}`,
      type: 'assistant',
      elements: [],
      isStreaming: true,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, typingMessage]);

    try {
      const response = await fetch(`/api/chat/threads/${threadId}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: currentMessage,
          conversationId,
        }),
      });

      if (!response.ok) throw new Error('Failed to send message to thread');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        
        // Keep the last incomplete line in buffer
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));              
              console.log('🎊 RECEIVED THREAD STREAMING DATA:', data);
              
              // Update conversation ID if received
              if (data.conversationId && !conversationId) {
                setConversationId(data.conversationId);
              }
                
              // For each new element, create a separate message bubble
              if (data.elements && data.elements.length > 0) {
                const newElements = data.elements;
                const existingElementIds = new Set();
                
                // Get existing element IDs to avoid duplicates
                setMessages(prev => {
                  prev.forEach(msg => {
                    if (msg.type === 'assistant' && msg.elements) {
                      msg.elements.forEach(el => existingElementIds.add(el.id));
                    }
                  });
                  
                  // Add new elements as separate messages
                  const newMessages = [...prev];
                  
                  // Remove typing indicators when real content arrives
                  const typingIndex = newMessages.findIndex(msg => 
                    msg.type === 'assistant' && msg.id.startsWith('typing-')
                  );
                  if (typingIndex !== -1) {
                    newMessages.splice(typingIndex, 1);
                  }
                  
                  // Stop streaming for all existing assistant messages when new content arrives
                  newMessages.forEach(msg => {
                    if (msg.type === 'assistant' && msg.isStreaming) {
                      msg.isStreaming = false;
                    }
                  });
                  
                  newElements.forEach(element => {
                    if (!existingElementIds.has(element.id)) {
                      newMessages.push({
                        id: `${Date.now()}-${element.id}`,
                        type: 'assistant',
                        elements: [element],
                        timestamp: new Date(),
                        isStreaming: !data.isComplete,
                      });
                    }
                  });
                  
                  return newMessages;
                });
              }
                
              if (data.isComplete) {
                // Stop all streaming indicators when conversation is complete
                setMessages(prev => prev.map(msg => 
                  msg.type === 'assistant' && msg.isStreaming 
                    ? { ...msg, isStreaming: false }
                    : msg
                ));
                loadChatThreads(); // Refresh threads list
                break;
              }
            } catch (e) {
              console.error('Error parsing thread streaming data:', e);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error sending message to thread:', error);
      // Remove typing indicator and add error message
      setMessages(prev => {
        const newMessages = prev.filter(msg => !msg.id.startsWith('typing-'));
        return [...newMessages, {
          id: Date.now(),
          type: 'assistant',
          elements: [{ id: 'error', type: 'text', content: 'Error occurred while processing your request.' }],
          timestamp: new Date(),
        }];
      });
    } finally {
      setIsLoading(false);
      // Ensure all streaming indicators are stopped
      setMessages(prev => prev.map(msg => 
        msg.type === 'assistant' && msg.isStreaming 
          ? { ...msg, isStreaming: false }
          : msg
      ));
    }
  };
  
  return (
    <>
      <div className="chat-container">
        {/* Mobile Sidebar Overlay */}
        <div 
          className={`chat-sidebar-overlay ${isMobileSidebarOpen ? 'active' : ''}`}
          onClick={closeMobileSidebar}
        ></div>
        
        <div className="chat-window">
          {/* Left Sidebar for Chat Threads */}
          <div className={`chat-sidebar ${isMobileSidebarOpen ? 'mobile-open' : ''}`}>          
            <div className="sidebar-header">
              <h1><i className="fas fa-history me-2"></i>SherBot.</h1>
            </div>
            
            <div className="threads-list">
              <button 
                className="new-thread-btn"
                onClick={() => createNewThread()}
                title="Start new chat"
              >
                <AddIcon style={{ fontSize: '16px', marginRight: '6px' }} />
                New Question
              </button>
              
              <div className="threads-scroll-container">
                {chatThreads.map(thread => (
                <div 
                  key={thread.id}
                  className={`thread-item ${currentThreadId === thread.id ? 'active' : ''}`}
                  onClick={() => loadThread(thread.id)}
                >
                  <div className="thread-content">
                    <div className="thread-title">{thread.title}</div>
                  </div>
                  <button 
                    className="thread-delete-btn"
                    onClick={(e) => deleteThread(thread.id, e)}
                    title="Delete chat thread"
                  >
                    <DeleteIcon style={{ fontSize: '16px' }} />
                  </button>
                </div>
              ))}
              {chatThreads.length === 0 && (
                <div className="no-threads">
                  <i className="fas fa-comments me-2"></i>
                  No chat history yet
                </div>
              )}
              </div>
            </div>
          </div>

          {/* Main Chat Area */}
          <div className="chat-main">
            {/* Mobile Menu Button */}
            <button 
              className="mobile-menu-floating-btn"
              onClick={toggleMobileSidebar}
              title="Open chat history"
            >
              <MenuIcon />
            </button>
            
            <div className="chat-messages" ref={messagesContainerRef}>
              {messages.length === 0 && (            
                <div className="chat-welcome">
                  <i className="fas fa-robot welcome-icon"></i>
                  <h2>Welcome to</h2>
                  <p>Your AI - powered copilot for <b>Sherbet Analytics</b></p>
                  {/* <div className="suggestion-chips">
                    <button 
                      className="suggestion-chip" 
                      onClick={() => setInputMessage("Show me 5 recent usernames")}
                    >
                      📧 Recent usernames
                    </button>
                    <button 
                      className="suggestion-chip" 
                      onClick={() => setInputMessage("How many users do we have?")}
                    >
                      👥 User count
                    </button>
                    <button 
                      className="suggestion-chip" 
                      onClick={() => setInputMessage("Show user activity trends")}
                    >
                      📈 Activity trends
                    </button>
                  </div> */}
                </div>
              )}
              
              {messages.map((message) => (
                <div key={message.id} className={`message ${message.type}`}>
                  <div className="message-content">
                    {message.type === 'user' ? (
                      <div className="user-message">
                        <i className="fas fa-user me-2"></i>
                        {message.content}
                      </div>                
                    ) : (
                      <div className={`assistant-message ${message.isStreaming ? 'streaming' : ''}`}>
                        <i className="fas fa-robot me-2"></i>
                        {message.elements?.map(renderElement)}
                        {message.isStreaming && (
                          <div className="typing-indicator">
                            <span></span><span></span><span></span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="message-time">
                    {message.timestamp.toLocaleTimeString()}
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>        
            
            <div className="chat-input">
              <div className="chat-input-container">
                <div className="input-wrapper">
                  <input
                    type="text"
                    className="chat-message-input"
                    placeholder="Ask me anything about your data..."
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}                
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        // Always use thread-based messaging (create new thread if needed)
                        handleSendMessageToThread();
                      }
                    }}
                    disabled={isLoading}
                  />              
                  <button
                    className="chat-send-button"
                    onClick={handleSendMessageToThread}
                    disabled={isLoading || !inputMessage.trim()}
                    type="button"
                  >
                    {isLoading ? (
                      <i className="fas fa-spinner fa-spin"></i>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M2 21L23 12L2 3V10L17 12L2 14V21Z" fill="currentColor"/>
                      </svg>
                    )}              
                  </button>
                </div>
                {isLoading && (
                  <div className="chat-loading-indicator">
                   
                  </div>
                )}
              </div>        
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={cancelDeleteThread}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h5 className="modal-title">
                <DeleteIcon style={{ marginRight: '8px', color: '#dc3545' }} />
                Delete Chat Thread
              </h5>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this chat thread?</p>
              <p className="text-muted">This action cannot be undone. All messages in this thread will be permanently deleted.</p>
            </div>
            <div className="modal-footer">
              <button 
                className="btn btn-secondary" 
                onClick={cancelDeleteThread}
              >
                Cancel
              </button>
              <button 
                className="btn btn-danger" 
                onClick={confirmDeleteThread}
              >
                <DeleteIcon style={{ fontSize: '16px', marginRight: '4px' }} />
                Delete Thread
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWindow;
